import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useSite } from "@/context/SiteContext";
import { resolveMediaUrl } from "@/lib/media";
import { motion, AnimatePresence } from "framer-motion";

const ALL_LINKS = [
  { to: "/", label: "Home", flag: null },
  { to: "/quienes-somos", label: "Quiénes Somos", flag: "nav_show_about" },
  { to: "/servicios", label: "Servicios", flag: "nav_show_services" },
  { to: "/talentos", label: "Talentos", flag: "nav_show_talents" },
  { to: "/productos", label: "Productos", flag: "nav_show_products" },
  { to: "/contacto", label: "Contacto", flag: "nav_show_contact" },
];

export default function Header() {
  const { theme, toggle } = useTheme();
  const { config, isPreview } = useSite();
  const [open, setOpen] = useState(false);

  const links = ALL_LINKS.filter((l) => !l.flag || isPreview || config?.[l.flag] !== false);
  const showContactCta = isPreview || config?.nav_show_contact !== false;
  const logoSrc = theme === "dark" ? config?.logo_dark : config?.logo_light;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="site-header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16 lg:h-20">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          {logoSrc ? (
            <img src={resolveMediaUrl(logoSrc)} alt="INFLUCAST" className="h-8 lg:h-9 w-auto object-contain" />
          ) : (
            <>
              <div className="w-8 h-8 rounded-sm bg-primary group-hover:rotate-12 transition-transform" />
              <span className="font-display font-black text-xl tracking-tighter">{config?.site_name || "INFLUCAST"}</span>
            </>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                  isActive ? "text-primary" : "text-foreground/70 hover:text-foreground"
                }`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="theme-toggle"
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Link
            to="/contacto"
            data-testid="header-cta"
            className={`${showContactCta ? "hidden md:inline-flex" : "hidden"} px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:opacity-90 transition-opacity`}
          >
            Trabajemos juntos
          </Link>
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden border-t border-border bg-background"
          >
            <div className="flex flex-col py-4 px-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-3 text-base font-medium border-b border-border ${
                      isActive ? "text-primary" : "text-foreground/80"
                    }`
                  }
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
